/**
 * CinematicHero — MISSION « HERO VIDÉO CINÉMATOGRAPHIQUE AUTOPLAY ».
 *
 * Architecture D (validée PO) : poster premium immédiat → chargement vidéo
 * en arrière-plan → crossfade imperceptible quand la lecture est RÉELLEMENT
 * stable → autoplay muted playsinline → boucle invisible (crossfade
 * fin→début encodé dans le master).
 *
 * Doctrine performance (NON NÉGOCIABLE) :
 *  1. Le POSTER (AVIF/WebP responsive, fetchpriority=high) EST le LCP.
 *     La vidéo n'entre JAMAIS dans le chemin critique.
 *  2. La vidéo n'est montée qu'après window.load + idle (preload auto à ce
 *     moment-là seulement) — play() programmatique sur canplay, crossfade
 *     déclenché uniquement quand la lecture est stable (playing + buffer).
 *  3. Fallbacks silencieux → poster premium, zéro message, zéro flash noir,
 *     zéro layout shift : prefers-reduced-motion, Save-Data, réseau 2g,
 *     source absente/404, autoplay refusé, décodage impossible.
 *
 * Remplace BrandFilm (SPRINT 7/8) : le mode « stills ciné-héros » (carousel
 * CSS de secours) est supprimé — le fallback est LE poster, jamais un
 * diaporama (directive mission : plus aucun carousel).
 */
import { useEffect, useRef, useState } from 'react';

const REDUCED = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const IS_MOBILE = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(max-width: 767px)').matches;

/* Économie de données : Save-Data explicite ou réseau très contraint →
   l'expérience est le poster premium (aucun téléchargement vidéo). */
const DATA_CONSTRAINED = () => {
  const c = typeof navigator !== 'undefined' && (navigator.connection || navigator.webkitConnection);
  if (!c) return false;
  if (c.saveData) return true;
  return c.effectiveType === 'slow-2g' || c.effectiveType === '2g';
};

export default function CinematicHero({
  poster,
  posterAvif = '',
  posterMobile = '',
  posterMobileAvif = '',
  mp4 = '',
  webm = '',
  mp4Mobile = '',
  webmMobile = '',
  alt = '',
  onProgress = null,
  className = '',
}) {
  // idle → arming (vidéo montée, en chargement) → live (crossfadée) | off
  const [phase, setPhase] = useState('idle');
  // Format évalué une seule fois au montage : un resize desktop↔mobile en
  // cours de lecture est un cas de test, pas un usage.
  const [mobile] = useState(IS_MOBILE);
  const videoRef = useRef(null);
  const playingRef = useRef(false);

  const activeMp4 = mobile && mp4Mobile ? mp4Mobile : mp4;
  const activeWebm = mobile && webmMobile ? webmMobile : webm;
  const hasSource = Boolean(activeMp4 || activeWebm);

  /* Armement : jamais avant window.load (le LCP et le contenu critique
     passent d'abord), puis à l'idle du navigateur. */
  useEffect(() => {
    if (!hasSource || REDUCED() || DATA_CONSTRAINED()) return;
    let cancelled = false;
    const arm = () => {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 350));
      idle(() => { if (!cancelled) setPhase('arming'); });
    };
    if (document.readyState === 'complete') arm();
    else window.addEventListener('load', arm, { once: true });
    return () => { cancelled = true; window.removeEventListener('load', arm); };
  }, [hasSource]);

  /* Pilotage de la lecture : play() programmatique dès canplay, crossfade
     seulement quand la lecture est stable (playing + readyState ≥ 3 ou
     canplaythrough). Tout échec → poster, silencieusement. */
  useEffect(() => {
    if (phase !== 'arming') return;
    const v = videoRef.current;
    if (!v) return;

    const off = () => setPhase('off');
    const goLive = () => setPhase('live');

    const tryPlay = () => {
      const p = v.play();
      if (p?.catch) p.catch(() => { /* autoplay refusé → poster, en silence */ });
    };
    const onPlaying = () => {
      playingRef.current = true;
      // HAVE_FUTURE_DATA minimum : on ne montre jamais une lecture qui va
      // geler dans la seconde. Sinon on attend canplaythrough.
      if (v.readyState >= 3) goLive();
    };
    const onCanPlayThrough = () => { if (playingRef.current) goLive(); };
    const onWaiting = () => { playingRef.current = false; };

    v.addEventListener('canplay', tryPlay, { once: true });
    v.addEventListener('playing', onPlaying);
    v.addEventListener('canplaythrough', onCanPlayThrough);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('error', off);
    // preload=auto posé APRÈS montage (jamais dans le HTML initial).
    v.preload = 'auto';
    v.load();

    return () => {
      v.removeEventListener('canplay', tryPlay);
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('canplaythrough', onCanPlayThrough);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('error', off);
    };
  }, [phase]);

  /* Progression → barre territoires (rythme = timeupdate natif ~4 Hz,
     le parent ne re-rend que si l'index de territoire change). */
  useEffect(() => {
    if (!onProgress || (phase !== 'live' && phase !== 'arming')) return;
    const v = videoRef.current;
    if (!v) return;
    const tick = () => onProgress(v.currentTime, v.duration || 0);
    v.addEventListener('timeupdate', tick);
    return () => v.removeEventListener('timeupdate', tick);
  }, [phase, onProgress]);

  const videoMounted = phase === 'arming' || phase === 'live';

  return (
    <div
      className={`absolute inset-0 overflow-hidden ${className}`}
      data-testid="cinematic-hero"
      data-phase={phase}
      data-mode={phase === 'live' ? 'video' : 'poster'}
      data-variant={mobile ? 'mobile' : 'desktop'}
    >
      {/* Poster = LCP. Toujours rendu — la vidéo fond par-dessus, il reste
          l'assise permanente (jamais de flash noir, même sur stall). */}
      <picture>
        {posterMobileAvif && (
          <source media="(max-width: 767px)" srcSet={posterMobileAvif} type="image/avif" />
        )}
        {posterMobile && (
          <source media="(max-width: 767px)" srcSet={posterMobile} type="image/webp" />
        )}
        {posterAvif && <source srcSet={posterAvif} type="image/avif" />}
        <img
          src={poster}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
          decoding="async"
          data-testid="cinematic-hero-poster"
        />
      </picture>

      {videoMounted && (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity [transition-duration:2600ms] [transition-timing-function:cubic-bezier(0.33,0,0.2,1)] ${
            phase === 'live' ? 'opacity-100' : 'opacity-0'
          }`}
          muted
          loop
          playsInline
          preload="none"
          // Doctrine Sprint 7 (contre-recette 17/09) — attribut poster en
          // défense en profondeur : même URL que l'<img> LCP (cache HTTP,
          // zéro requête supplémentaire). L'assise visuelle reste l'<img>
          // toujours rendue ; l'attribut couvre le cas où la vidéo serait
          // visible avant sa première frame.
          poster={mobile && posterMobile ? posterMobile : poster}
          aria-hidden="true"
          tabIndex={-1}
          data-testid="cinematic-hero-video"
        >
          {activeWebm && <source src={activeWebm} type="video/webm" onError={activeMp4 ? undefined : () => setPhase('off')} />}
          {activeMp4 && <source src={activeMp4} type="video/mp4" onError={() => setPhase('off')} />}
        </video>
      )}
    </div>
  );
}
